import React from 'react';
import { RadioGroup } from '@headlessui/react';
import { CheckCircleIcon } from '@heroicons/react/20/solid';

// Function to format date as 'Day of the Week, Month Day at Time'
const formatDateTime = (date: Date): string => {
  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const dayOfWeek = daysOfWeek[date.getDay()];
  const month = months[date.getMonth()];
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const formattedTime = `${hours % 12 || 12}:${minutes} ${ampm}`;

  return `${dayOfWeek}, ${month} ${day} at ${formattedTime}`;
};

interface AvailableSlot {
  providerId: string;
  datetime: string;
}

interface AvailableSlotsProps {
  slots: AvailableSlot[];
  selectedSlot: AvailableSlot | null;
  onChange: (slot: AvailableSlot | null) => void;
}

const AvailableSlots: React.FC<AvailableSlotsProps> = ({ slots, selectedSlot, onChange }) => {
  return (
    <fieldset>
      <RadioGroup
        value={selectedSlot}
        onChange={onChange}
        className="mt-6 grid grid-cols-1 gap-y-6 sm:grid-cols-3 sm:gap-x-4"
      >
        {slots.map((slot) => (
          <RadioGroup.Option
            key={slot.providerId + slot.datetime}
            value={slot}
            aria-label={`Time slot at ${formatDateTime(new Date(slot.datetime))}`}
            className="group relative flex cursor-pointer rounded-lg border border-gray-300 bg-white p-4 shadow-sm focus:outline-none data-[focus]:border-deep-blue data-[focus]:ring-2 data-[focus]:ring-deep-blue"
          >
            <span className="flex flex-1">
              <span className="flex flex-col">
                <span className="block text-sm font-medium text-gray-900">
                  {formatDateTime(new Date(slot.datetime))}
                </span>
              </span>
            </span>
            <CheckCircleIcon
              aria-hidden="true"
              className="h-5 w-5 text-deep-blue [.group:not([data-checked])_&]:invisible"
            />
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -inset-px rounded-lg border-2 border-transparent group-data-[focus]:border group-data-[checked]:border-deep-blue"
            />
          </RadioGroup.Option>
        ))}
      </RadioGroup>
    </fieldset>
  );
};

export default AvailableSlots;
